import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import FormuleSection from "@/sections/FormuleSection";
import { useLang } from "@/i18n";
import { menuCategories } from "@/config/menu";
import { useContent } from "@/content/ContentContext";
import { scrollToHash } from "@/App";

const MenuPage = () => {
  const { t } = useLang();
  const m = t.menu;
  const { site } = useContent();

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
  }, []);

  return (
    <main className="pt-28 md:pt-40 pb-24 md:pb-36" data-testid="menu-page">
      <div className="max-w-4xl mx-auto px-5 md:px-10">
        <Reveal>
          <p className="eyebrow text-olive mb-7">{m.label}</p>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight" data-testid="menu-title">
            {m.title}
          </h1>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            className="mt-12 bg-bordeaux text-ivory p-6 md:p-8"
            data-testid="menu-placeholder-banner"
          >
            <p className="text-sm md:text-base font-light leading-relaxed text-ivory/80">
              {m.banner}
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <nav
            className="mt-12 flex gap-3 overflow-x-auto pb-2 -mx-5 px-5 md:mx-0 md:px-0 md:flex-wrap"
            aria-label={m.label}
            data-testid="menu-category-nav"
          >
            {menuCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => scrollToHash(`#menu-cat-${cat.id}`)}
                data-testid={`menu-cat-nav-${cat.id}`}
                className="whitespace-nowrap rounded-full border border-anthracite/25 px-5 py-2 text-sm transition-colors duration-200 hover:border-bordeaux hover:text-bordeaux"
              >
                {m.categories[cat.id]}
              </button>
            ))}
          </nav>
        </Reveal>

        <Reveal delay={0.12}>
          <p
            className="mt-8 flex items-start gap-4 font-serif italic text-base md:text-lg text-anthracite/75 leading-snug"
            data-testid="menu-formula-note"
          >
            <span className="mt-3 block w-8 h-px bg-bordeaux/60 shrink-0" aria-hidden="true" />
            {m.formulaNote}
          </p>
        </Reveal>

        <FormuleSection compact />

        {site.menuPdf && (
          <a
            href={site.menuPdf}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="menu-download-button"
            className="mt-8 inline-flex items-center gap-2 text-sm underline underline-offset-4 decoration-sand hover:text-bordeaux transition-colors duration-200"
          >
            {m.download}
            <ArrowUpRight className="w-4 h-4" />
          </a>
        )}

        <div className="mt-14 md:mt-20 space-y-16 md:space-y-20">
          {menuCategories.map((cat, ci) => (
            <Reveal key={cat.id} delay={0.05}>
              <section id={`menu-cat-${cat.id}`} data-testid={`menu-category-${cat.id}`}>
                <div className="flex items-baseline gap-5">
                  <span className="font-serif italic text-2xl text-sand">
                    {String(ci + 1).padStart(2, "0")}
                  </span>
                  <h2 className="font-serif text-3xl md:text-4xl tracking-tight">
                    {m.categories[cat.id]}
                  </h2>
                </div>
                <div className="mt-7 space-y-6">
                  {cat.items.map((item, i) => (
                    <div key={i} className={item.name ? "" : "opacity-60"}>
                      <div className="flex items-baseline" data-testid={`menu-item-${cat.id}-${i}`}>
                        <span className={`text-base md:text-lg ${item.name ? "" : "italic text-anthracite/60"}`}>
                          {item.name || m.placeholderItem}
                        </span>
                        <span className="menu-leader" aria-hidden="true" />
                        <span className="text-base md:text-lg font-serif">
                          {item.price || "—"}
                        </span>
                      </div>
                      <p className="mt-1 text-sm font-light text-anthracite/55 italic">
                        {item.description || m.placeholderDesc}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-20">
          <Link
            to="/"
            data-testid="menu-back-home"
            className="inline-flex items-center gap-2 text-sm text-anthracite/60 hover:text-bordeaux transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            {m.backHome}
          </Link>
        </Reveal>
      </div>
    </main>
  );
};

export default MenuPage;
